import React from 'react';
import {
  Form,
  Input,
  Row,
  Col,
  Switch,
  Select,
  Upload,
  Divider,
  Button,
} from 'antd';
import {
  PhoneOutlined,
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { FormInstance } from 'antd/lib/form/Form';
// Types
import { AccountSubType, StoreAthleticLevel } from 'api/graphql-global-types';
import { Sports } from 'api/sports/types/Sports';
// Constants
import {
  USER_SUBTYPES,
  SOCIALS_OPTIONS,
} from 'constants/userCreationConstants';
// Helpers
import { handlePlaceSelect } from 'helpers/place';
import { getSocialLinkInputError } from './helpers';
// Components
import { SocialAccount } from './AdminCreateUserForm';
import PlaceInput from 'components/common/PlaceInput/PlaceInput';
// Style
import styles from './AdminCreateUserForm.module.scss';

const { Option } = Select;
const { TextArea } = Input;

interface OnboardingDataFormProps {
  form: FormInstance;
  sportsData: Sports | undefined;
  loadingSports: boolean;
  avatarFile: File | null;
  setAvatarFile: (file: File | null) => void;
  socialAccounts: SocialAccount[];
  setSocialAccounts: React.Dispatch<React.SetStateAction<SocialAccount[]>>;
}

const OnboardingDataForm: React.FC<OnboardingDataFormProps> = ({
  form,
  sportsData,
  loadingSports,
  avatarFile,
  setAvatarFile,
  socialAccounts,
  setSocialAccounts,
}) => {
  const handleAvatarChange = (info: any) => {
    if (info.file) {
      const file = info.file.originFileObj || info.file;
      setAvatarFile(file);
      form.setFieldsValue({ avatarValidation: 'uploaded' });
    }
    form.validateFields(['avatarValidation']);
  };

  const handleSocialAccountChange = (
    index: number,
    field: string,
    value: string
  ) => {
    const updated = [...socialAccounts];
    updated[index] = {
      ...updated[index],
      [field]: value,
      linkError: undefined,
    };
    setSocialAccounts(updated);
  };

  const addSocialAccount = (accountIndex: number) => {
    const accountToAdd = socialAccounts[accountIndex];

    if (accountToAdd.isAdded) {
      return;
    }

    const trimmedLink = accountToAdd.link.trim();
    const linkError = getSocialLinkInputError(
      accountToAdd.platform,
      trimmedLink
    );
    if (linkError) {
      setSocialAccounts((prevState) =>
        prevState.map((account, index) =>
          index === accountIndex ? { ...account, linkError } : account
        )
      );
      return;
    }

    setSocialAccounts((prevState) =>
      prevState.map((account, index) =>
        index === accountIndex
          ? {
              ...account,
              link: trimmedLink,
              isAdded: true,
              linkError: undefined,
            }
          : account
      )
    );
    form.validateFields(['socialAccountsValidation']);
  };

  const addNewSocialAccountSlot = () => {
    setSocialAccounts([
      ...socialAccounts,
      { platform: undefined, link: '', isAdded: false },
    ]);
    form.validateFields(['socialAccountsValidation']);
  };

  const removeSocialAccount = (index: number) => {
    if (socialAccounts.length === 1) {
      setSocialAccounts([
        {
          platform: undefined,
          link: '',
          isAdded: false,
        },
      ]);
    } else {
      setSocialAccounts(socialAccounts.filter((_: any, i: any) => i !== index));
    }
    form.validateFields(['socialAccountsValidation']);
  };

  return (
    <>
      <Divider orientation="left">Profile Information</Divider>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="accountType"
            label="Account Type"
            rules={[{ required: true, message: 'Account type is required' }]}
          >
            <Select placeholder="Select account type" labelInValue>
              {USER_SUBTYPES.map((subtype) => (
                <Option key={subtype.value} value={subtype.value}>
                  {subtype.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="sport"
            label="Primary Sport"
            rules={[{ required: true, message: 'Primary sport is required' }]}
          >
            <Select
              placeholder="Select primary sport"
              showSearch
              optionFilterProp="children"
              loading={loadingSports}
            >
              {sportsData?.sports?.map((sport) => (
                <Option key={sport.id} value={sport.id}>
                  {sport.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) =>
          prevValues.accountType !== currentValues.accountType
        }
      >
        {({ getFieldValue }) => {
          const accountType = getFieldValue('accountType');
          return accountType?.value === AccountSubType.Athlete ? (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="experienceLevel"
                  label="Experience Level"
                  rules={[
                    {
                      required: true,
                      message: 'Experience level is required',
                    },
                  ]}
                >
                  <Select placeholder="Select experience level">
                    <Option value={StoreAthleticLevel.Amateur}>Amateur</Option>
                    <Option value={StoreAthleticLevel.CollegeUniversity}>
                      College/University
                    </Option>
                    <Option value={StoreAthleticLevel.Professional}>
                      Professional
                    </Option>
                    <Option value={StoreAthleticLevel.RetiredProfessional}>
                      Retired Professional
                    </Option>
                    <Option value={StoreAthleticLevel.OlympicNational}>
                      Olympic/National
                    </Option>
                    <Option value={StoreAthleticLevel.Other}>Other</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          ) : null;
        }}
      </Form.Item>

      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="bio" label="Bio">
            <TextArea
              rows={4}
              placeholder="Tell us about yourself (max 300 characters)"
              maxLength={300}
              showCount
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="avatarValidation"
        label="Avatar"
        rules={[
          {
            validator: async () => {
              if (!avatarFile) {
                return Promise.reject(new Error('Avatar is required'));
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Upload
          beforeUpload={() => false}
          onChange={handleAvatarChange}
          accept="image/*"
          maxCount={1}
          onRemove={() => {
            setAvatarFile(null);
            form.setFieldsValue({ avatarValidation: null });
            form.validateFields(['avatarValidation']);
          }}
        >
          <Button icon={<UploadOutlined />}>Upload Avatar</Button>
        </Upload>
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="phoneNumber"
            label="Phone Number"
            rules={[{ required: true, message: 'Phone number is required' }]}
          >
            <Input prefix={<PhoneOutlined />} placeholder="Phone Number" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="phoneOptIn"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) => {
                  if (value === true) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error('You must opt-in for phone notifications')
                  );
                },
              },
            ]}
          >
            <div className={styles.phoneOptInContainer}>
              <Switch
                onChange={(checked) => {
                  form.setFieldsValue({ phoneOptIn: checked });
                }}
              />
              <span style={{ marginLeft: 8 }}>
                Opt-in for phone notifications
              </span>
            </div>
          </Form.Item>
        </Col>
      </Row>

      {/* Location Section */}
      <Divider orientation="left">Location</Divider>

      <Form.Item
        name="citySearch"
        label="Enter location"
        rules={[{ required: true, message: 'Please select a location' }]}
      >
        <PlaceInput
          value={form.getFieldValue('citySearch')}
          onChange={(value) => form.setFieldsValue({ citySearch: value })}
          onPlaceSelect={(placeDetails) => {
            const locationString = handlePlaceSelect(
              form.setFieldsValue,
              placeDetails,
              'city'
            );
            form.setFieldsValue({ citySearch: locationString });
            form.validateFields(['citySearch']);
          }}
        />
      </Form.Item>

      <Form.Item name="country" label="Country">
        <Input disabled />
      </Form.Item>

      {form.getFieldValue('state') && (
        <Form.Item name="state" label="State">
          <Input disabled />
        </Form.Item>
      )}

      <Form.Item name="city" label="City">
        <Input disabled />
      </Form.Item>

      <Form.Item name="latitude" noStyle />
      <Form.Item name="longitude" noStyle />

      <Divider orientation="left">Social Media</Divider>

      <Form.Item
        name="socialAccountsValidation"
        className={styles.socialValidation}
        rules={[
          {
            validator: async () => {
              const hasAddedAccount = socialAccounts.some(
                (account) => account.isAdded
              );
              if (!hasAddedAccount) {
                return Promise.reject(
                  new Error('At least one social media account is required')
                );
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <span style={{ opacity: 0, height: 0, fontSize: 0 }}>validation</span>
      </Form.Item>

      {socialAccounts.map((account: any, index: any) => (
        <div key={index}>
          {account.isAdded ? (
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label={index === 0 ? 'Platform' : ''}>
                  <Input
                    value={
                      SOCIALS_OPTIONS.find(
                        (opt) => opt.value === account.platform
                      )?.label || ''
                    }
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={index === 0 ? 'Profile Link' : ''}
                  validateStatus={account.linkError ? 'error' : undefined}
                  help={account.linkError}
                >
                  <Input value={account.link} disabled />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label={index === 0 ? '\u00A0' : undefined}>
                  <Button
                    danger
                    className={styles.removeSocialLink}
                    icon={<DeleteOutlined />}
                    onClick={() => removeSocialAccount(index)}
                  />
                </Form.Item>
              </Col>
            </Row>
          ) : (
            // Input for new social accounts
            <Row gutter={16} align="middle">
              <Col span={8}>
                <Form.Item label={index === 0 ? 'Platform' : ''}>
                  <Select
                    placeholder="Select platform"
                    value={account.platform}
                    onChange={(value) =>
                      handleSocialAccountChange(index, 'platform', value)
                    }
                  >
                    {SOCIALS_OPTIONS.map((option) => (
                      <Option
                        key={option.value}
                        value={option.value}
                        disabled={socialAccounts.some(
                          (acc: any) =>
                            acc.platform === option.value && acc.isAdded
                        )}
                      >
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item
                  label={index === 0 ? 'Profile Link' : ''}
                  validateStatus={account.linkError ? 'error' : undefined}
                  help={account.linkError}
                >
                  <Input
                    placeholder="https://..."
                    value={account.link}
                    onChange={(e) =>
                      handleSocialAccountChange(index, 'link', e.target.value)
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={index === 0 ? ' ' : ''}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() => addSocialAccount(index)}
                      disabled={!account.platform}
                    >
                      Add
                    </Button>
                  </div>
                </Form.Item>
              </Col>
            </Row>
          )}
        </div>
      ))}

      {socialAccounts.length < SOCIALS_OPTIONS.length &&
        socialAccounts[socialAccounts.length - 1]?.isAdded && (
          <Row>
            <Col span={24}>
              <Button
                type="dashed"
                className={styles.addAnotherSocial}
                icon={<PlusOutlined />}
                onClick={addNewSocialAccountSlot}
              >
                Add Another Social Account
              </Button>
            </Col>
          </Row>
        )}
    </>
  );
};

export default OnboardingDataForm;
